import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Select, Switch, message } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import { Button as Btn } from 'react-bootstrap';
import { feedbackService } from '../../services/feedback/feedbackService';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';

const { Option } = Select;

interface SensitiveWord {
  _id: string;
  word: string;
  level: 'block' | 'review' | 'replace';
  replaceWith: string;
  description: string;
  enabled: boolean;
}

const levelPill = (level: SensitiveWord['level']) => {
  const map: Record<SensitiveWord['level'], { cls: string; text: string }> = {
    block: { cls: 'rm-fb-level-pill rm-fb-level-pill--block', text: '拦截' },
    review: { cls: 'rm-fb-level-pill rm-fb-level-pill--review', text: '需审核' },
    replace: { cls: 'rm-fb-level-pill rm-fb-level-pill--replace', text: '替换' },
  };
  const x = map[level];
  return <span className={x.cls}>{x.text}</span>;
};

const FeedbackSettingsPage: React.FC = () => {
  const location = useLocation();
  const isPlatformAdmin = location.pathname.startsWith('/platform-admin');
  const [data, setData] = useState<SensitiveWord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<SensitiveWord | null>(null);
  const [form] = Form.useForm();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const list = await feedbackService.listSensitiveWords();
      setData(list);
    } catch (error: any) {
      message.error(error.message || '获取敏感词列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateModal = () => {
    setEditing(null);
    form.resetFields();
    setModalVisible(true);
  };

  const openEditModal = (record: SensitiveWord) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('确定删除该敏感词规则？')) return;
    try {
      await feedbackService.deleteSensitiveWord(id);
      message.success('删除成功');
      loadData();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await feedbackService.updateSensitiveWord(editing._id, values);
        message.success('更新成功');
      } else {
        await feedbackService.createSensitiveWord(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadData();
    } catch (error: any) {
      if (error?.message) {
        message.error(error.message);
      }
    }
  };

  return (
    <div className="page-container full-height members-lims fb-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        {isPlatformAdmin ? (
          <>
            <span>平台管理</span>
            <span className="mm-crumb-sep">/</span>
            <span>匿名反馈治理</span>
            <span className="mm-crumb-sep">/</span>
            <span aria-current="page">敏感词配置</span>
          </>
        ) : (
          <>
            <Link to="/feedback/square" className="fb-crumb-link">
              畅所欲言
            </Link>
            <span className="mm-crumb-sep">/</span>
            <span aria-current="page">敏感词配置</span>
          </>
        )}
      </nav>

      <header className="mm-page-head">
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 w-100">
          <div>
            <h1 className="mm-title">敏感词配置</h1>
            <p className="mm-subtitle mb-0">维护拦截、审核与替换规则，与成员管理界面一致。</p>
          </div>
          <Btn variant="primary" className="rounded-pill d-inline-flex align-items-center gap-2" onClick={openCreateModal}>
            <LimsNavIcon name="add" sizePx={18} />
            新增敏感词
          </Btn>
        </div>
      </header>

      <div className="mm-table-shell">
        <div className="mm-sheet-scroll">
          <table className="mm-sheet">
            <thead>
              <tr>
                <th>词语</th>
                <th>级别</th>
                <th>替换为</th>
                <th>启用</th>
                <th>说明</th>
                <th className="mm-th-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="mm-empty text-center py-4">
                    加载中…
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="mm-empty">
                    暂无规则，请点击右上方新增
                  </td>
                </tr>
              ) : (
                data.map((record) => (
                  <tr key={record._id} className="mm-row">
                    <td>
                      <p className="mm-identity-name mb-0">{record.word}</p>
                    </td>
                    <td>{levelPill(record.level)}</td>
                    <td className="small">{record.replaceWith || '—'}</td>
                    <td>{record.enabled ? '是' : '否'}</td>
                    <td className="small text-muted text-break" style={{ maxWidth: 280 }}>
                      {record.description || '—'}
                    </td>
                    <td>
                      <div className="mm-actions">
                        <button type="button" className="mm-icon-action" title="编辑" onClick={() => openEditModal(record)}>
                          <LimsNavIcon name="edit" sizePx={22} />
                        </button>
                        <button type="button" className="mm-icon-action" title="删除" onClick={() => handleDelete(record._id)}>
                          <LimsNavIcon name="delete_outline" sizePx={22} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={modalVisible} title={editing ? '编辑敏感词' : '新增敏感词'} onOk={handleSubmit} onCancel={() => setModalVisible(false)} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="word" label="词语" rules={[{ required: true, message: '请输入敏感词' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="level" label="级别" initialValue="review" rules={[{ required: true, message: '请选择级别' }]}>
            <Select>
              <Option value="block">拦截（禁止发布）</Option>
              <Option value="review">需审核</Option>
              <Option value="replace">替换</Option>
            </Select>
          </Form.Item>
          <Form.Item name="replaceWith" label="替换为（级别为替换时生效）" initialValue="***">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="enabled" label="是否启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FeedbackSettingsPage;
